import { MAX_SNIPPET_LENGTH } from "./publisher-nav-protocol";

// Shared with changes.ts's snippet floor: below this a needle matches most of
// the page's copy rather than the edited element.
export const MIN_USEFUL_SPAN = 3;

/** Folds the differences between JSX source text and rendered copy — curly
 *  quotes, &nbsp; (covered by \s), and case — so the two can be compared. */
export function foldText(text: string): string {
  return text
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

const ENTITIES: Record<string, string> = {
  "&nbsp;": " ",
  "&amp;": "&",
  "&quot;": '"',
  "&apos;": "'",
  "&lt;": "<",
  "&gt;": ">",
  "&mdash;": "—",
  "&ndash;": "–",
  "&hellip;": "…",
  "&lsquo;": "‘",
  "&rsquo;": "’",
  "&ldquo;": "“",
  "&rdquo;": "”",
  "&copy;": "©",
  "&reg;": "®",
  "&trade;": "™",
  "&times;": "×",
  "&middot;": "·",
  "&bull;": "•",
};

// Marks a spot the source gives no readable character for. Runs break here
// rather than closing over it: two shorter runs each still match their element
// by containment, where one run carrying a wrong character matches nothing.
const BREAK = "\u0000";

const ENTITY = /&(?:#x[0-9a-f]+|#\d+|[a-z][a-z0-9]*);/gi;

export function decodeEntities(text: string): string {
  return text.replace(ENTITY, (entity) => {
    const named = ENTITIES[entity.toLowerCase()];
    if (named) return named;
    const numeric = /^&#(x)?([0-9a-f]+);$/i.exec(entity);
    if (!numeric) return BREAK;
    const code = parseInt(numeric[2], numeric[1] ? 16 : 10);
    return code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : BREAK;
  });
}

// Tags and interpolated expressions both end a run of visible text: a tag can
// put the next characters in a different element (textContent joins siblings
// with no separator, so a needle spanning one never matches), and an
// expression's value isn't knowable from source.
//
// Finding a tag's end means stepping over braces and quotes rather than
// stopping at the first `>`, which an arrow-function handler
// (`onClick={() => submit()}`) or a `>` inside an attribute puts there ahead
// of the real one.
const BRACED = String.raw`\{(?:[^{}]|\{[^{}]*\})*\}`;
const TOKENS = new RegExp(
  String.raw`<(?:[^>{}"']|"[^"]*"|'[^']*'|${BRACED})*>|${BRACED}`,
  "g",
);

// Structural attributes whose value never renders as element text. Anything
// else on a tag is treated as a component prop that may carry copy, which is
// where a lot of this codebase's headings and button labels live.
const NON_COPY_ATTRS = new Set([
  "style",
  "id",
  "key",
  "ref",
  "href",
  "src",
  "srcset",
  "sizes",
  "type",
  "name",
  "rel",
  "target",
  "role",
  "as",
  "d",
  "viewbox",
  "fill",
  "stroke",
  "xmlns",
  "width",
  "height",
  "loading",
  "slug",
  "path",
  "variant",
  "size",
  "color",
  "align",
  "direction",
  "theme",
  "icon",
  "value",
]);

const ATTR_VALUE =
  /([a-zA-Z_$][\w$:.-]*)\s*=\s*(?:"([^"]*)"|'([^']*)'|\{\s*(?:"((?:[^"\\\n]|\\.)*)"|'((?:[^'\\\n]|\\.)*)'|(?:t|msg)?`((?:[^`\\]|\\.)*)`)\s*\})/g;

function isCopyAttr(name: string): boolean {
  return !(
    NON_COPY_ATTRS.has(name) ||
    name.endsWith("class") ||
    name.endsWith("classname") ||
    name.startsWith("data-") ||
    name.startsWith("aria-")
  );
}

// Only a component's props can reach the page as text. On a native element
// every attribute is markup - placeholder, title and alt render as chrome, not
// as textContent, which is all the bridge matches - so none of it is copy.
// JSX already draws the line for us: lower-case names are DOM elements.
const COMPONENT_TAG = /^<\s*[A-Z]/;

function tagCopy(tag: string): string[] {
  return COMPONENT_TAG.test(tag) ? attrCopy(tag) : [];
}

function attrCopy(tag: string): string[] {
  const runs: string[] = [];
  for (const m of tag.matchAll(ATTR_VALUE)) {
    if (!isCopyAttr(m[1].toLowerCase())) continue;
    // Only a braced value is a JS string, so only it can hold an escape.
    runs.push(m[2] ?? m[3] ?? decodeEscapes(m[4] ?? m[5] ?? m[6] ?? ""));
  }
  return runs;
}

// Inside a tag or an expression every quote is a code delimiter, so all three
// kinds are safe to unwrap.
const CODE_QUOTED =
  /"((?:[^"\\\n]|\\.)*)"|'((?:[^'\\\n]|\\.)*)'|`((?:[^`\\]|\\.)*)`/g;

const ESCAPE =
  /\\(u\{[0-9a-fA-F]+\}|u[0-9a-fA-F]{4}|x[0-9a-fA-F]{2}|\r\n|[\s\S])/g;
const SIMPLE_ESCAPES: Record<string, string> = {
  b: "\b",
  f: "\f",
  r: "\r",
  t: "\t",
  v: "\v",
  "0": "\0",
};

// The page shows the decoded character. A `\n` becomes a mark: Markdown splits
// lines there, anything else renders it as whitespace.
function decodeEscapes(value: string): string {
  return value.replace(ESCAPE, (escape, code: string) => {
    if (code === "n") return NEWLINE_MARK;
    if (code === "\n" || code === "\r\n" || code === "\r") return "";
    if (code[0] === "u" || code[0] === "x") {
      const hex = code.replace(/^[ux]\{?|\}$/g, "");
      const point = parseInt(hex, 16);
      return point <= 0x10ffff ? String.fromCodePoint(point) : escape;
    }
    return SIMPLE_ESCAPES[code] ?? code;
  });
}

function stringContent(m: RegExpMatchArray): string {
  return decodeEscapes(m[1] ?? m[2] ?? m[3] ?? "");
}

function codeStrings(fragment: string): string[] {
  return [...fragment.matchAll(CODE_QUOTED)].map(stringContent);
}

// Copy just as often lives in a constant as in JSX ("const HEADLINE = ..."),
// where there is no tag to split on and the whole statement would otherwise
// become one unmatchable run.
const TEXT_QUOTED =
  /"((?:[^"\\\n]|\\.)*)"|`((?:[^`\\]|\\.)*)`|'((?:[^'\\\n]|\\.)*)'/g;
// Only a quote an operator opens is a code string. Without this, prose that
// quotes a phrase - <p>We call it "the only helpdesk"</p> - would yield the
// phrase alone, and the bridge would prefer whichever unrelated element
// matches it exactly over the paragraph that actually changed.
const OPENS_STRING = /[=:(,[{]\s*$/;
// An apostrophe is far commoner in prose than a single-quoted string is, so a
// single quote wants an assignment, property or call in front of it: a comma
// is enough for the other two but reads as ordinary punctuation here.
const OPENS_SINGLE = /[=:({]\s*$/;
const OPENS_TAGGED = /\b(?:t|msg)$/;
// Not `in`/`of`/`do`/`else`: prose puts those before a quoted phrase too.
const OPENS_EXPRESSION =
  /(?:(?:^|[^\w$])(?:return|case|yield|await|throw)\s|=>|\?|\s\+|&&|\|\|)\s*$/;
// Prose runs on after a quoted phrase; an operand is followed by punctuation.
const CLOSES_EXPRESSION = /^[ \t]*(?:[;,)\]}:?+]|&&|\|\||\r?\n|$)/;
// An edit can start on a long string whose `content:` prettier left on the line above.
// Requiring its trailing comma keeps quoted JSX text (`<p>"Start now"</p>`) quoted.
const LITERAL_LINE_END = /^[)\]}\s]*[,;][,;)\]}\s]*(?:\n|$)/;
// Every opener above fits in this many characters, so a longer look back only costs time.
const OPENER_REACH = 40;

function opensString(text: string, m: RegExpMatchArray): boolean {
  const index = m.index ?? 0;
  const before = text.slice(Math.max(0, index - OPENER_REACH), index);
  const after = text.slice(index + m[0].length);
  if (OPENS_EXPRESSION.test(before) && CLOSES_EXPRESSION.test(after))
    return true;
  if (m[3] !== undefined) return OPENS_SINGLE.test(before);
  if (OPENS_STRING.test(before)) return true;
  if (m[2] !== undefined && OPENS_TAGGED.test(before)) return true;
  return index === text.search(/\S/) && LITERAL_LINE_END.test(after);
}

function codeStringMatches(text: string): RegExpMatchArray[] {
  return [...text.matchAll(TEXT_QUOTED)].filter((m) => opensString(text, m));
}

interface Run {
  text: string;
  quoted: boolean;
}

function unwrapCodeStrings(text: string): Run[] {
  const runs = codeStringMatches(text).map((m) => ({
    text: stringContent(m),
    quoted: true,
  }));
  return runs.length ? runs : [{ text, quoted: false }];
}

// Deeper nesting than this is never real copy, and unbounded it overflows the stack.
const MAX_NESTING = 32;

function stringEnd(text: string, start: number, depth = 0): number {
  const quote = text[start];
  for (let i = start + 1; i < text.length; i++) {
    const c = text[i];
    if (c === "\\") i++;
    else if (c === quote) return i;
    else if (c === "\n" && quote !== "`") return -1;
    else if (quote === "`" && c === "$" && text[i + 1] === "{") {
      i = interpolationEnd(text, i + 2, depth + 1);
      if (i < 0) return -1;
    }
  }
  return -1;
}

function commentEnd(text: string, start: number): number {
  if (text[start + 1] === "/") return text.indexOf("\n", start);
  const close = text.indexOf("*/", start + 2);
  return close < 0 ? -1 : close + 1;
}

function regexEnd(text: string, start: number): number {
  let inClass = false;
  for (let i = start + 1; i < text.length; i++) {
    const c = text[i];
    if (c === "\\") i++;
    else if (c === "\n") return -1;
    else if (c === "[") inClass = true;
    else if (c === "]") inClass = false;
    else if (c === "/" && !inClass) return i;
  }
  return -1;
}

// A `/` after one of these starts a regex literal; anywhere else it divides.
const REGEX_PRECEDER = /[(,=:[!&|?{;+\-*/%~^<>]/;
const REGEX_KEYWORD =
  /(?:^|[^\w$])(?:return|typeof|instanceof|in|of|new|delete|void|throw|case|do|else|yield|await)\s*$/;

function opensRegex(text: string, start: number, i: number, prev: string) {
  return (
    REGEX_PRECEDER.test(prev) ||
    REGEX_KEYWORD.test(text.slice(Math.max(start, i - OPENER_REACH), i))
  );
}

function interpolationEnd(text: string, start: number, nesting = 0): number {
  if (nesting > MAX_NESTING) return -1;
  let depth = 1;
  let prev = "{";
  for (let i = start; i < text.length; i++) {
    const c = text[i];
    if (c === '"' || c === "'" || c === "`") {
      i = stringEnd(text, i, nesting);
      prev = c;
    } else if (c === "/" && (text[i + 1] === "/" || text[i + 1] === "*")) {
      i = commentEnd(text, i);
    } else if (c === "/" && opensRegex(text, start, i, prev)) {
      i = regexEnd(text, i);
      prev = ")";
    } else {
      if (c === "{") depth++;
      else if (c === "}" && --depth === 0) return i;
      if (!/\s/.test(c)) prev = c;
    }
    if (i < 0) return -1;
  }
  return -1;
}

function isEscaped(text: string, index: number): boolean {
  let slashes = 0;
  while (text[index - 1 - slashes] === "\\") slashes++;
  return slashes % 2 === 1;
}

// A `\${…}` becomes a literal hole, restored as the text it renders.
function holeInterpolations(source: string, values: string[]): string {
  let out = "";
  let cursor = 0;
  for (let i = source.indexOf("${"); i >= 0; i = source.indexOf("${", cursor)) {
    const end = interpolationEnd(source, i + 2);
    // Unclosed, it runs to the end of the edit, so every later `${` sits inside it.
    if (end < 0) break;
    const escaped = isEscaped(source, i);
    const mark = escaped ? LITERAL_MARK : VALUE_MARK;
    values.push(source.slice(i, end + 1));
    out += `${source.slice(cursor, escaped ? i - 1 : i)}${mark}${values.length - 1}${mark}`;
    cursor = end + 1;
  }
  return out + source.slice(cursor);
}

// HTML tag names are lower case, so a plain-text prop's literal "<Tab>" survives.
// The bridge skips these elements' subtrees, so their contents go too.
const HTML_MARKUP =
  /<!--[\s\S]*?-->|<(script|style|noscript|template)\b(?:[^<>"']|"[^"]*"|'[^']*')*>[\s\S]*?<\/\1\s*>|<\/?[a-z][a-z0-9-]*(?=[\s/>])(?:[^<>"']|"[^"]*"|'[^']*')*>/g;
// An escaped `<` or `>` still reaches the HTML parser as a bracket.
const ESCAPED_BRACKET =
  /(?<!\\)((?:\\\\)*)\\(?:u003([cCeE])|x3([cCeE])|u\{0*3([cCeE])\})/g;

function unescapeBrackets(literal: string): string {
  return literal.replace(
    ESCAPED_BRACKET,
    (_m, slashes: string, a?: string, b?: string, c?: string) =>
      slashes + ((a ?? b ?? c ?? "").toLowerCase() === "c" ? "<" : ">"),
  );
}

const VALUE_MARK = "\u0001";
const LITERAL_MARK = "\u0002";
const VALUE_HOLE = /\u0001\d+\u0001/g;
const HOLE = /([\u0001\u0002])(\d+)\1/g;
const NEWLINE_MARK = "\u0003";
const MARK_CHARS = /[\u0001\u0002\u0003]/g;

interface FlatSource {
  text: string;
  restore: (run: string) => string;
}

// Markup in a string renders via dangerouslySetInnerHTML, where it joins into one textContent.
function flattenCodeStrings(source: string): FlatSource {
  const values: string[] = [];
  const text = holeInterpolations(source.replace(MARK_CHARS, ""), values);
  // A string inside the value can render too: `${open ? "Hide" : "Show"} all`.
  const valueRuns = (hole: string) => {
    const value = values[Number(hole.slice(1, -1))] ?? "";
    const inner = flattenCodeStrings(value.slice(2, -1));
    const runs = codeStrings(inner.text).map(
      (run) =>
        `${LITERAL_MARK}${values.push(inner.restore(run)) - 1}${LITERAL_MARK}`,
    );
    return [BREAK, ...runs, BREAK].join(BREAK);
  };
  let flat = "";
  let cursor = 0;
  for (const m of codeStringMatches(text)) {
    const start = m.index ?? 0;
    // Only a template substitutes `${…}`; a quoted string renders it as written.
    const literal =
      m[2] === undefined ? m[0] : m[0].replace(VALUE_HOLE, valueRuns);
    flat +=
      text.slice(cursor, start).replace(HOLE, BREAK) +
      unescapeBrackets(literal).replace(HTML_MARKUP, "");
    cursor = start + m[0].length;
  }
  return {
    text: flat + text.slice(cursor).replace(HOLE, BREAK),
    restore: (run) =>
      run.replace(
        HOLE,
        (hole, _mark: string, i: string) => values[Number(i)] ?? hole,
      ),
  };
}

// Markdown in a data file renders without its syntax, each `\n`-separated line as its own element.
const DECODED_NEWLINE = /\u0003/g;
const MARKDOWN_BLOCK_MARKER = /^(?:[-*+]|\d+\.|#{1,6}|>)\s+/;
const MARKDOWN_LINK = /!?\[([^\]\n]*)\]\([^)\s]*\)/g;
// No `~~`: the FAQ renderer has no GFM plugin, so strikethrough shows literally.
const MARKDOWN_INLINE_MARKER = /\*\*|__|`/g;
const JSX_STRING_EXPRESSION = /^\{\s*(["'`])[^]*\1\s*\}$/;
const MARKDOWN_EMPHASIS = /(^|[^\w*])[*_]([^*_\n]+)[*_](?![\w*])/g;

// The visible text a chunk of source renders, split where the rendered DOM
// also splits it.
function proseRuns(raw: string): string[] {
  const { text: source, restore } = flattenCodeStrings(raw);
  const runs: Run[] = [];
  let cursor = 0;
  let jsx = false;
  for (const token of source.matchAll(TOKENS)) {
    if (token.index > cursor) {
      runs.push(...unwrapCodeStrings(source.slice(cursor, token.index)));
    }
    const text = token[0];
    if (text.startsWith("<") || JSX_STRING_EXPRESSION.test(text)) jsx = true;
    const copy = text.startsWith("<") ? tagCopy(text) : codeStrings(text);
    runs.push(...copy.map((run) => ({ text: run, quoted: true })));
    cursor = token.index + text.length;
  }
  if (cursor < source.length) {
    runs.push(...unwrapCodeStrings(source.slice(cursor)));
  }

  return runs.flatMap((run) => {
    // JSX renders asterisks and `\n` literally; only data-file strings go through a Markdown renderer.
    const markdown = run.quoted && !jsx;
    const lines = markdown
      ? restore(run.text).split(DECODED_NEWLINE)
      : [restore(run.text).replace(DECODED_NEWLINE, " ")];
    // A one-line string like "1. TECH PLAN" is literal copy; stripping its number would hide a renumber.
    const list = lines.length > 1;
    return lines.flatMap((line) =>
      decodeEntities(line)
        .split(BREAK)
        .map((part) => {
          const text = (markdown ? stripInlineMarkdown(part) : part)
            .replace(/\s+/g, " ")
            .trim();
          return list ? text.replace(MARKDOWN_BLOCK_MARKER, "") : text;
        })
        .filter(Boolean),
    );
  });
}

function stripInlineMarkdown(part: string): string {
  return part
    .replace(MARKDOWN_LINK, "$1")
    .replace(MARKDOWN_INLINE_MARKER, "")
    .replace(MARKDOWN_EMPHASIS, "$1$2");
}

function sentences(run: string): string[] {
  return run
    .split(/(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

// changes.ts drops a needle over the protocol's limit rather than clipping it,
// so a one-word edit inside a long paragraph would go unhighlighted. Fall back
// to the sentences that changed - each still lands on the same element by
// containment.
function narrow(run: string, previous: readonly string[]): string[] {
  if (run.length <= MAX_SNIPPET_LENGTH) return [run];
  const before = new Set(previous.flatMap(sentences));
  const parts = sentences(run);
  const changed = parts.filter((s) => !before.has(s));
  return (changed.length ? changed : parts).map(clip);
}

// A single sentence can run past the limit on its own. A leading slice still
// lands on the right element - nothing unrelated shares this many characters -
// where dropping it loses the highlight outright.
function clip(sentence: string): string {
  if (sentence.length <= MAX_SNIPPET_LENGTH) return sentence;
  const head = sentence.slice(0, MAX_SNIPPET_LENGTH);
  const lastSpace = head.lastIndexOf(" ");
  return lastSpace > MIN_USEFUL_SPAN ? head.slice(0, lastSpace) : head;
}

function counts(runs: readonly string[]): Map<string, number> {
  const map = new Map<string, number>();
  for (const run of runs) map.set(run, (map.get(run) ?? 0) + 1);
  return map;
}

// The runs of `subject` a reader would see as new or reworded. Diffing whole
// runs instead of characters makes the context Publisher's agent pads an edit's
// oldString with drop out on its own: an untouched paragraph is an identical
// run, leaving only the edited one to point at.
export function changedRuns(subject: string, other: string): string[] {
  const next = proseRuns(subject);
  const previous = proseRuns(other);

  // Counted rather than set membership, and case-sensitive: copy repeated
  // twice and edited once leaves the other occurrence behind, and "Get
  // started" to "Get Started" is a change a reader sees.
  const remaining = counts(previous);
  const added: string[] = [];
  for (const run of next) {
    const left = remaining.get(run) ?? 0;
    if (left > 0) remaining.set(run, left - 1);
    else added.push(run);
  }
  if (added.length) {
    return [...new Set(added.flatMap((run) => narrow(run, previous)))];
  }

  // A pure deletion leaves nothing new to point at, so anchor to whichever
  // neighbouring run survived - that element is the one that lost the text.
  // Tested before the reorder case below: a deleted run shifts every later run
  // up an index, which otherwise reads as a reorder and points at the run
  // after the gap - a different, unedited element.
  if (next.length < previous.length) {
    const surviving = counts(next);
    const removed = previous.findIndex((run) => {
      const left = surviving.get(run) ?? 0;
      if (left === 0) return true;
      surviving.set(run, left - 1);
      return false;
    });
    if (removed < 0) return [];
    const neighbour = [previous[removed - 1], previous[removed + 1]].find(
      (run) => run && next.includes(run),
    );
    return neighbour ? [neighbour] : [];
  }

  // Same runs in a different order - nothing is new, but the page reads
  // differently, so point at the first run that moved.
  const moved = next.findIndex((run, i) => run !== previous[i]);
  return moved >= 0 ? [next[moved]] : [];
}
